import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShareIcon, 
  GlobeAltIcon, 
  EnvelopeIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';
import { getApiUrl } from '../config/api';
import ScrollToTopLink from './ScrollToTopLink';
import { useFooterNavigation } from '../hooks/useFooterNavigation';

// Use SVGs for Facebook, Twitter, Instagram
const FacebookIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true"><path d="M22.675 0h-21.35C.595 0 0 .592 0 1.326v21.348C0 23.408.595 24 1.325 24h11.495v-9.294H9.692v-3.622h3.128V8.413c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0"/></svg>
);
const TwitterIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true"><path d="M24 4.557a9.83 9.83 0 0 1-2.828.775 4.932 4.932 0 0 0 2.165-2.724c-.951.564-2.005.974-3.127 1.195a4.916 4.916 0 0 0-8.38 4.482C7.691 8.095 4.066 6.13 1.64 3.161c-.542.929-.856 2.01-.857 3.17 0 2.188 1.115 4.116 2.823 5.247a4.904 4.904 0 0 1-2.229-.616c-.054 2.281 1.581 4.415 3.949 4.89a4.936 4.936 0 0 1-2.224.084c.627 1.956 2.444 3.377 4.6 3.417A9.867 9.867 0 0 1 0 21.543a13.94 13.94 0 0 0 7.548 2.209c9.058 0 14.009-7.496 14.009-13.986 0-.213-.005-.425-.014-.636A9.936 9.936 0 0 0 24 4.557z"/></svg>
);
const InstagramIcon = () => (
  <svg fill="currentColor" viewBox="0 0 24 24" className="h-6 w-6" aria-hidden="true"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 1.366.062 2.633.334 3.608 1.308.974.974 1.246 2.242 1.308 3.608.058 1.266.069 1.646.069 4.85s-.012 3.584-.07 4.85c-.062 1.366-.334 2.633-1.308 3.608-.974.974-2.242 1.246-3.608 1.308-1.266.058-1.646.069-4.85.069s-3.584-.012-4.85-.07c-1.366-.062-2.633-.334-3.608-1.308-.974-.974-1.246-2.242-1.308-3.608C2.175 15.747 2.163 15.367 2.163 12s.012-3.584.07-4.85c.062-1.366.334-2.633 1.308-3.608.974-.974 2.242-1.246 3.608-1.308C8.416 2.175 8.796 2.163 12 2.163zm0-2.163C8.741 0 8.332.013 7.052.072 5.775.131 4.602.425 3.635 1.392 2.668 2.359 2.374 3.532 2.315 4.809 2.256 6.089 2.243 6.498 2.243 12c0 5.502.013 5.911.072 7.191.059 1.277.353 2.45 1.32 3.417.967.967 2.14 1.261 3.417 1.32 1.28.059 1.689.072 7.191.072s5.911-.013 7.191-.072c1.277-.059 2.45-.353 3.417-1.32.967-.967 1.261-2.14 1.32-3.417.059-1.28.072-1.689.072-7.191s-.013-5.911-.072-7.191c-.059-1.277-.353-2.45-1.32-3.417C21.05.425 19.877.131 18.6.072 17.32.013 16.911 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zm0 10.162a3.999 3.999 0 1 1 0-7.998 3.999 3.999 0 0 1 0 7.998zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/></svg>
);

const Footer = () => {
  const [categories, setCategories] = useState([]);
  const { navigateWithScrollToTopReact } = useFooterNavigation();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await fetch(getApiUrl('/api/admin/categories'));
        const data = await res.json();
        if (Array.isArray(data.categories)) setCategories(data.categories);
        else if (Array.isArray(data)) setCategories(data);
        else setCategories([]);
      } catch (err) {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-2 sm:px-4 md:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-8">
          {/* Company Info */}
          <div className="space-y-2 sm:space-y-4">
            <h3 className="text-lg sm:text-xl md:text-2xl font-bold">theVastra.store</h3>
            <p className="text-gray-400 text-sm sm:text-base">
              Your one-stop destination for trendy and affordable fashion.
            </p>
            <div className="flex space-x-4">
              <a href="https://www.facebook.com/profile.php?id=61579074161205" target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="text-gray-400 hover:text-white">
                <FacebookIcon />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter" className="text-gray-400 hover:text-white">
                <TwitterIcon />
              </a>
              <a href="https://www.instagram.com/thevastra.store/profilecard/?igsh=dGFwNnl6bmF0bmdx" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-gray-400 hover:text-white">
                <InstagramIcon />
              </a>
            </div>
          </div>

          {/* Quick Links - Only policy/support pages */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-4">Quick Links</h4>
         
            <ul className="space-y-1 sm:space-y-2">
              <li>
                <button 
                  onClick={() => navigateWithScrollToTopReact('/catalogue')}
                  className="text-gray-400 hover:text-white text-left text-sm sm:text-base"
                >
                  Catalogue
                </button>
              </li>
              <li>
                <button 
                  onClick={() => navigateWithScrollToTopReact('/reviews')}
                  className="text-gray-400 hover:text-white text-left text-sm sm:text-base"
                >
                  Reviews
                </button>
              </li>
              <li>
                <button 
                  onClick={() => navigateWithScrollToTopReact('/support')}
                  className="text-gray-400 hover:text-white text-left text-sm sm:text-base"
                >
                  Customer Support
                </button>
              </li>
              <li>
                <button 
                  onClick={() => navigateWithScrollToTopReact('/terms-of-service')}
                  className="text-gray-400 hover:text-white text-left text-sm sm:text-base"
                >
                  Terms of Service
                </button>
              </li>
              <li>
                <button 
                  onClick={() => navigateWithScrollToTopReact('/privacy-policy')}
                  className="text-gray-400 hover:text-white text-left text-sm sm:text-base"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button 
                  onClick={() => navigateWithScrollToTopReact('/cancellation-policy')}
                  className="text-gray-400 hover:text-white text-left text-sm sm:text-base"
                >
                  Cancellation & Refund
                </button>
              </li>
            </ul>
          </div>

          {/* Categories - Dynamic */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-4">Categories</h4>
            <ul className="space-y-1 sm:space-y-2">
              {categories.length === 0 ? (
                <li className="text-gray-500 text-sm sm:text-base">No categories found.</li>
              ) : (
                categories.map(category => (
                  <li key={category._id}>
                    <button 
                      onClick={() => navigateWithScrollToTopReact(`/products?category=${category.name.toLowerCase()}`)}
                      className="text-gray-400 hover:text-white text-left text-sm sm:text-base"
                    >
                      {category.name}
                    </button>
                  </li>
                ))
              )}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-base sm:text-lg font-semibold mb-2 sm:mb-4">Contact Us</h4>
            <ul className="space-y-1 sm:space-y-2 text-gray-400 text-sm sm:text-base">
              <li>Opposite SBS Medical College</li>
              <li>Balawala, Dehradun, Uttarakhand 248008</li>
              <li>Phone: (+91) 8077369863</li>
              <li>Email: thevastrastore1999@gmail.com</li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-6 sm:mt-8 md:mt-12 pt-4 sm:pt-6 md:pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-xs sm:text-sm">
              © 2024 theVastra.store. All rights reserved.
            </p>
         
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 